/* FATCUTPRESS Theme JavaScript
 * Cart drawer, product variants, and page interactions,
 * product lightbox, and mobile gallery counter.
 */

(() => {
  'use strict';

  const root = window.Shopify?.routes?.root || '/';

  const money = cents => new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    maximumFractionDigits: 0
  }).format((Number(cents) || 0) / 100);

  const drawerLayer = () =>
    document.querySelector('[data-cart-layer]');


  const isCartReviewPage = () =>
    Boolean(document.querySelector('[data-cart-page]'));

  let cartMutation = Promise.resolve();
  let checkoutSubmitStarted = false;

  function queueCartMutation(task){
    cartMutation = cartMutation
      .catch(() => {})
      .then(task);

    return cartMutation;
  }

  async function submitCheckout(){
    if(checkoutSubmitStarted){
      return;
    }

    checkoutSubmitStarted = true;

    try{
      await cartMutation;
    }catch(error){
      console.error(error);
      checkoutSubmitStarted = false;
      return;
    }

    const form = document.querySelector('[data-cart-checkout-form]');
    const button = form?.querySelector('[name="checkout"]');

    if(!form){
      checkoutSubmitStarted = false;
      return;
    }

    const completeCheckout = () => {
      form.dataset.audioFadeComplete = 'true';

      if(typeof form.requestSubmit === 'function'){
        form.requestSubmit(button || undefined);
        return;
      }

      form.submit();
    };

    /* The cart review button is handled in this file, so explicitly route it
       through the same checkout fade coordinator used by native checkout
       submits. This restores the full 1.5-second fade on desktop and mobile. */
    if(window.FatcutCheckout?.run){
      window.FatcutCheckout.run(completeCheckout);
    }else{
      completeCheckout();
    }
  }

  /* =========================================================
     Cart data and drawer rendering
     ========================================================= */

  async function getCart(){
    const response = await fetch(root + 'cart.js', {
      headers: {
        'Accept': 'application/json'
      }
    });

    if(!response.ok){
      throw new Error('Cart could not be loaded');
    }

    return response.json();
  }

  function updateCartCount(count){
    document
      .querySelectorAll('[data-cart-count]')
      .forEach(element => {
        const amount = Number(count) || 0;
        element.textContent = amount;
        element.hidden = amount === 0;
        element.setAttribute('aria-hidden', String(amount === 0));
      });
  }

  function setCartToggleState(isOpen){
    document
      .querySelectorAll('[data-cart-toggle]')
      .forEach(toggle => {
        toggle.classList.toggle('is-cart-open', isOpen);
        toggle.setAttribute('aria-expanded', String(isOpen));
        toggle.setAttribute('aria-label', isOpen ? 'Close cart' : 'Open cart');
      });
  }

  async function renderDrawer(){
    const layer = drawerLayer();

    if(!layer){
      return;
    }

    const items =
      layer.querySelector('[data-cart-items]');

    const subtotal =
      layer.querySelector('[data-cart-subtotal]');

    const shipping =
      layer.querySelector('[data-cart-shipping]');

    const total =
      layer.querySelector('[data-cart-total]');

    try{
      const cart = await getCart();

      updateCartCount(cart.item_count);

      if(!cart.items.length){
        items.innerHTML = `
          <div class="cart-drawer__empty">
            <p>No items in your cart.</p>
            <a href="${root}collections/all" data-cart-continue-shopping>
              CONTINUE SHOPPING
            </a>
          </div>
        `;

        subtotal.textContent = money(0);
        shipping.textContent = money(0);
        total.textContent = money(0);

        return;
      }

      items.innerHTML = cart.items
        .map((item, index) => `
          <article
            class="cart-drawer-item"
            data-line="${index + 1}"
          >
            <div class="cart-drawer-item__visual">
              ${
                item.image
                  ? `
                    <img
                      src="${item.image}"
                      alt="${item.product_title.replace(/"/g, '&quot;')}"
                      loading="lazy"
                    >
                  `
                  : ''
              }
            </div>

            <div class="cart-drawer-item__content">
              <div class="cart-drawer-item__meta">
                ${item.variant_title || ''}
              </div>

              <h3>${item.product_title}</h3>

              <div class="cart-drawer-item__bottom">
                <div class="cart-drawer-quantity">
                  <button
                    type="button"
                    data-cart-minus
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>

                  <span>${item.quantity}</span>

                  <button
                    type="button"
                    data-cart-plus
                    aria-label="Increase quantity"
                  >
                    ＋
                  </button>
                </div>

                <span class="cart-drawer-item__price">
                  ${money(item.final_line_price)}
                </span>
              </div>

              <button
                class="cart-drawer-item__remove"
                type="button"
                data-cart-remove
              >
                REMOVE
              </button>
            </div>
          </article>
        `)
        .join('');

      const fixedShipping = 100000;

      subtotal.textContent =
        money(cart.total_price);

      shipping.textContent =
        money(fixedShipping);

      total.textContent =
        money(cart.total_price + fixedShipping);

    }catch(error){
      console.error(error);
    }
  }

  /* =========================================================
     Cart drawer open / close
     ========================================================= */

  function openDrawer(){
    const layer = drawerLayer();

    if(
      !layer ||
      layer.classList.contains('is-open')
    ){
      return;
    }

    renderDrawer();
    setCartToggleState(true);

    layer.classList.add('is-active');
    layer.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        layer.classList.add('is-open');
      });
    });
  }

  function closeDrawer(){
    const layer = drawerLayer();

    if(
      !layer ||
      !layer.classList.contains('is-active')
    ){
      return;
    }

    layer.classList.remove('is-open');
    layer.setAttribute('aria-hidden', 'true');
    setCartToggleState(false);

    let finished = false;

    const finish = () => {
      if(
        finished ||
        layer.classList.contains('is-open')
      ){
        return;
      }

      finished = true;
      layer.classList.remove('is-active');
    };

    layer
      .querySelector('.cart-drawer')
      ?.addEventListener(
        'transitionend',
        finish,
        { once: true }
      );

    setTimeout(finish, 520);
  }

  window.FatcutCart = Object.assign(window.FatcutCart || {}, {
    open: openDrawer,
    close: closeDrawer,
    render: renderDrawer
  });

  async function changeLine(line, quantity){
    const response = await fetch(
      root + 'cart/change.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          line,
          quantity
        })
      }
    );

    if(!response.ok){
      throw new Error(
        'Cart quantity could not be updated'
      );
    }

    await renderDrawer();
  }

  async function addVariant(variantId){
    const response = await fetch(
      root + 'cart/add.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1
            }
          ]
        })
      }
    );

    if(!response.ok){
      throw new Error(
        'Unable to add product'
      );
    }

    openDrawer();
  }

  /* =========================================================
     Product variants and add-to-cart
     ========================================================= */

  function setupProduct(section){
    const variantsNode =
      section.querySelector('[data-product-variants]');

    const idInput =
      section.querySelector('[data-variant-id]');

    const addButton =
      section.querySelector('[data-add-product]');

    if(
      !variantsNode ||
      !idInput ||
      !addButton
    ){
      return;
    }

    const variants =
      JSON.parse(variantsNode.textContent);

    const optionGroups = [
      ...section.querySelectorAll(
        '[data-option-position]'
      )
    ];

    function selectedOptions(){
      return optionGroups.map(group => {
        return (
          group
            .querySelector('.is-active')
            ?.dataset.value || ''
        );
      });
    }

    function syncOptionAvailability(selected){
      optionGroups.forEach((group, groupIndex) => {
        group
          .querySelectorAll('[data-option-value]')
          .forEach(button => {
            const value = button.dataset.value;
            const isAvailable = variants.some(variant => {
              if(!variant.available || variant.options[groupIndex] !== value){
                return false;
              }

              return variant.options.every((optionValue, optionIndex) => {
                return optionIndex === groupIndex || optionValue === selected[optionIndex];
              });
            });

            button.disabled = !isAvailable;
            button.classList.toggle('is-unavailable', !isAvailable);
            if(!isAvailable){
              button.classList.remove('is-active');
            }
            button.setAttribute('aria-disabled', String(!isAvailable));
          });
      });
    }

    function syncVariant(){
      const selected =
        selectedOptions();

      syncOptionAvailability(selected);

      const variant =
        variants.find(item => {
          return item.options.every(
            (value, index) =>
              value === selected[index]
          );
        });

      if(!variant){
        addButton.disabled = true;
        addButton.textContent = 'UNAVAILABLE';
        return;
      }

      idInput.value = variant.id;

      addButton.disabled =
        !variant.available;

      addButton.textContent =
        variant.available
          ? 'ADD TO CART'
          : 'SOLD OUT';

      const price =
        section.querySelector(
          '[data-product-price]'
        );

      if(price){
        price.textContent =
          money(variant.price);
      }
    }

    optionGroups.forEach(group => {
      group.addEventListener(
        'click',
        event => {
          const button =
            event.target.closest(
              '[data-option-value]'
            );

          if(!button || button.disabled){
            return;
          }

          group
            .querySelectorAll(
              '[data-option-value]'
            )
            .forEach(item => {
              item.classList.remove(
                'is-active'
              );
            });

          button.classList.add(
            'is-active'
          );

          syncVariant();
        }
      );
    });

    addButton.addEventListener(
      'click',
      async event => {
        event.preventDefault();

        if(addButton.disabled){
          return;
        }

        addButton.disabled = true;

        try{
          await addVariant(idInput.value);
        }catch(error){
          console.error(error);
        }finally{
          syncVariant();
        }
      }
    );

    syncVariant();
  }

  /* =========================================================
     Global cart interactions
     ========================================================= */

  function stopBackgroundScroll(event){
    const layer = drawerLayer();

    if(!layer?.classList.contains('is-open')){
      return;
    }

    if(event.target.closest?.('.cart-drawer')){
      return;
    }

    event.preventDefault();
  }

  document.addEventListener(
    'click',
    event => {
      const cartToggle =
        event.target.closest?.('[data-cart-toggle]');

      if(cartToggle){
        event.preventDefault();

        const isOpen =
          drawerLayer()?.classList.contains('is-open');

        if(isOpen){
          closeDrawer();
        }else{
          openDrawer();
        }

        return;
      }

      const checkoutReturn =
        event.target.closest?.('[data-cart-return-checkout]');

      if(checkoutReturn){
        event.preventDefault();
        submitCheckout();
        return;
      }

      const closeButton =
        event.target.closest?.('[data-cart-close]');

      if(closeButton){
        event.preventDefault();
        closeDrawer();
        return;
      }

      const lineItem =
        event.target.closest?.('[data-line]');

      if(!lineItem){
        return;
      }

      const line = Number(lineItem.dataset.line);
      const quantity = Number(
        lineItem
          .querySelector('.cart-drawer-quantity span')
          ?.textContent || 1
      );

      if(event.target.closest?.('[data-cart-minus]')){
        queueCartMutation(() =>
          changeLine(line, Math.max(0, quantity - 1))
        ).catch(console.error);
        return;
      }

      if(event.target.closest?.('[data-cart-plus]')){
        queueCartMutation(() =>
          changeLine(line, quantity + 1)
        ).catch(console.error);
        return;
      }

      if(event.target.closest?.('[data-cart-remove]')){
        queueCartMutation(() =>
          changeLine(line, 0)
        ).catch(console.error);
      }
    }
  );

  document.addEventListener(
    'keydown',
    event => {
      if(event.key !== 'Escape'){
        return;
      }

      closeDrawer();
    }
  );

  window.addEventListener(
    'wheel',
    stopBackgroundScroll,
    { passive: false }
  );

  window.addEventListener(
    'touchmove',
    stopBackgroundScroll,
    { passive: false }
  );

  async function initializeDynamicContent(){
    document
      .querySelectorAll('[data-product-section]:not([data-fatcut-initialized])')
      .forEach(section => {
        section.dataset.fatcutInitialized = 'true';
        setupProduct(section);
      });

    try{
      const cart = await getCart();
      updateCartCount(cart.item_count);
    }catch(error){
      console.error(error);
    }
  }

  window.FatcutThemeInit = initializeDynamicContent;
  document.addEventListener('DOMContentLoaded', initializeDynamicContent);

  /* =========================================================
     Page content interactions
     ========================================================= */

  function handleProductAccordion(event){
    const trigger =
      event.target.closest(
        '.fatcut-accordion__trigger'
      );

    if(!trigger){
      return;
    }

    const item =
      trigger.closest(
        '.fatcut-accordion__item'
      );

    const accordion =
      trigger.closest(
        '.fatcut-accordion'
      );

    if(
      !item ||
      !accordion
    ){
      return;
    }

    const isOpen =
      item.classList.contains(
        'is-open'
      );

    accordion
      .querySelectorAll(
        '.fatcut-accordion__item'
      )
      .forEach(otherItem => {
        otherItem.classList.remove(
          'is-open'
        );

        const otherTrigger =
          otherItem.querySelector(
            '.fatcut-accordion__trigger'
          );

        if(otherTrigger){
          otherTrigger.setAttribute(
            'aria-expanded',
            'false'
          );
        }
      });

    if(!isOpen){
      item.classList.add(
        'is-open'
      );

      trigger.setAttribute(
        'aria-expanded',
        'true'
      );
    }
  }

  document.addEventListener(
    'click',
    event => {
      handleProductAccordion(event);
    }
  );

  // Product lightbox and mobile gallery are initialized exclusively by fatcut-barba.js.

})();